// North Carolina State University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - NC State's Common Data Set 2025-2026, the Google Doc that University
//    Data and Analytics links from its Common Data Set page (the SOURCES url
//    is that ncsu.edu page): C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C8 test policy for Fall 2027, C21/C22 plans.
//  - NC State Undergraduate Admissions: First-Year Applicants, Dates and
//    Deadlines (first-year timeline, studio-based majors, music technology),
//    Test Score Consideration in Admission Decisions, Application Review and
//    FAQs (first-choice-only majors). The deadline table carries no year; it
//    is the table posted while the FAQ page says "You can now apply for fall
//    2027", next to the 2027-2028 FAFSA note.
//  - NC State Student Services Center, Undergraduate Student Estimated Cost of
//    Attendance, Academic Year 2026-27, living on campus.
//  - College Scorecard, school id 199193 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost quirk: the CDS G1 block is headed 2026-2027, but its tuition ($6,535),
// required fees ($2,493), housing ($8,335) and food ($6,406) are the aid
// office's 2025-26 figures, and its G5 leaves resident other expenses blank.
// So the page uses the aid office's own 2026-27 on-campus budget, which lists
// tuition and fees as one line and includes $44 in loan fees; its totals
// equal the sum of its items.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'NC State Common Data Set 2025-2026',
        url: 'https://uda.ncsu.edu/institutional-analytics/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'NC State Admissions, First-Year Applicants',
        url: 'https://admissions.ncsu.edu/apply/first-year/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'NC State Admissions, Dates and Deadlines',
        url: 'https://admissions.ncsu.edu/apply/deadlines/',
        read: '2026-10-01',
    },
    tests: {
        label: 'NC State Admissions, Test Score Consideration in Admission Decisions',
        url: 'https://admissions.ncsu.edu/apply/first-year/test-score-consideration-in-admission-decisions/',
        read: '2026-10-01',
    },
    review: {
        label: 'NC State Admissions, Application Review',
        url: 'https://admissions.ncsu.edu/apply/first-year/application-review/',
        read: '2026-10-01',
    },
    faq: {
        label: 'NC State Admissions, Frequently Asked Questions',
        url: 'https://admissions.ncsu.edu/connect/frequently-asked-questions/',
        read: '2026-10-01',
    },
    costs: {
        label: 'NC State Student Services Center, Undergraduate Student Estimated Cost of Attendance',
        url: 'https://studentservices.ncsu.edu/finances/estimated-cost-of-attendance/undergraduate-student-estimated-cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?199193-North-Carolina-State-University-at-Raleigh',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'north-carolina-state-university',
    kbId: 'north_carolina_state_university',
    name: 'North Carolina State University',
    shortName: 'NC State',
    aliases: ['NC State', 'NCSU'],
    location: 'Raleigh, NC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (brand.ncsu.edu color page); decoration only.
    branding: {
        primary: '#CC0000',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/north_carolina_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 49351,
        admitted: 19514,
        enrolled: 5906,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 24140, admitted: 11159 },
            { group: 'Out-of-state', applied: 21853, admitted: 7509 },
            { group: 'International', applied: 3358, admitted: 846 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'NC State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major, although NC State admits you directly into a major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'NC State has no early decision plan. Its early action is nonbinding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'NC State admits you directly into a major: you pick a first and a second choice, ideally in two different colleges, and if you are not competitive for the first you are reviewed for the second. Some majors ask for more:',
        rows: [
            { school: 'Studio-based majors (Architecture; Media Arts, Design and Technology; Graphic & Experience Design; Industrial Design; Fashion and Textile Design)', requirement: 'One deadline, November 1, with a portfolio due the same day (in wolfPAW; Fashion and Textile Design uses SlideRoom). Fall entry only; decisions come on a rolling basis by January 31. NC State encourages you to apply by mid-October to leave time for the portfolio.', source: 'deadlines' },
            { school: 'Music Technology', requirement: 'Fall entry only. A portfolio of no more than five samples of your work (each video or audio sample up to 5 minutes) and an additional essay, both in wolfPAW. You can apply early action or regular decision.', source: 'deadlines' },
            { school: 'First-choice-only majors', requirement: 'Engineering majors, Applied Math, Mathematics, Physics and the studio-based majors can be chosen only as your first-choice major, not your second.', source: 'faq' },
        ],
        source: 'review',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'North Carolina residents by the end of December; all others January 31', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 31', binding: false },
            { plan: 'Studio-based majors', deadline: 'November 1', decision: 'Rolling, by January 31', binding: false },
        ],
        note: 'The application opens August 1. Transcripts, test scores and other materials are due November 10 for early action and studio-based majors and February 1 for regular decision; you must meet both deadlines. North Carolina residents must confirm residency through the Residency Determination Service by November 10 to get a December decision. Early action applicants get priority consideration for scholarships and financial aid. Admitted students confirm by May 1.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'NC State is test-optional for first-year applicants with a weighted GPA of 2.8 or above: you say on the application whether you want SAT, ACT or CLT scores considered, and you are not disadvantaged if you do not. With a weighted GPA from 2.5 up to 2.8, a score is required: an ACT of 17 or higher or an SAT of 930 or higher. NC State superscores, and you self-report scores when you apply.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and required fees', amounts: [9247, 34961] },
            { item: 'Housing (on campus)', amounts: [8738, 8738] },
            { item: 'Food', amounts: [6684, 6684] },
            { item: 'Books and supplies', amounts: [642, 642] },
            { item: 'Transportation', amounts: [1485, 2060] },
            { item: 'Personal expenses', amounts: [1650, 1650] },
            { item: 'Loan fees', amounts: [44, 44] },
        ],
        totals: [28490, 54779],
        note: 'NC State\'s estimate for a full-time undergraduate living on campus, fall and spring. NC State lists tuition and fees as one figure.',
        source: 'costs',
        netPrice: {
            average: 16486,
            byIncome: [
                { income: '$0 to $30,000', amount: 6149 },
                { income: '$30,001 to $48,000', amount: 6914 },
                { income: '$48,001 to $75,000', amount: 10322 },
                { income: '$75,001 to $110,000', amount: 18201 },
                { income: 'Over $110,000', amount: 25674 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from NC State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
